// React
import { Fragment, useEffect, useState } from "react";
import clsx from "clsx";
import { SnackbarKey, useSnackbar } from "notistack";

// Styles
import "@/assets/styles/settings/PositionMapping.css";

// Redux
// React-Query
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// MUI
import { Box, Button, CircularProgress, Divider, Skeleton, Stack, Typography } from "@mui/material";

// APIs
import {
	applyMappedPositions,
	getPositionsMapping,
	onCreateUsers,
	onUpdateSystemSettingsPositionMapping,
	updateSystemSettingsMappedPositions,
} from "@Settings/api/PositionMapping";

// Hooks
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Actions
import actions from "@Constants/actionsList";

// Components
import PositionMappingSection from "./PositionMappingSection";
import BodyLayer from "@Layouts/components/Layers/BodyLayer";
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import { SnackbarsActions, SnackbarsContent } from "@/Layouts/components/Snackbars";

// Types
import { IMappedPositionInput } from "@Settings/types/PositionMapping/types";
import { IBasicMappedPosition } from "@Common/types/positions/api";
import { IPositionsMapping, LabelObject } from "@Settings/types/PositionMapping/model";
import { useLocationId } from "@Common/hooks/Location/useLocationId.tsx";

type TSubscriptionResponse = {
	data: {
		onUpdateSystemSettingsPositions: {
			mappedPositions: IBasicMappedPosition[];
			requiredPositions: IBasicMappedPosition[];
			accountingPositions: string[];
			locationId: string;
		};
	};
};

type TOtherClientSubscriptionResponse = {
	data: {
		onCreateUsers: {
			locationId: string;
		};
	};
};

/********************************************** HELPERS **********************************************/
function getAvailablePositions(positionsMapping: IPositionsMapping): LabelObject[] {
	const allPayRollPositions = [
		...positionsMapping.managedPositions,
		...positionsMapping.requiredPositions,
		...positionsMapping.accountingPositions,
	].reduce((acc, mappedPosition) => {
		return acc.concat(mappedPosition.payrollPosition);
	}, [] as string[]);

	return positionsMapping.availablePositions.filter((labelObj) => !allPayRollPositions.includes(labelObj.label));
}

function arePositionsUnmapped(mappedPositions: IBasicMappedPosition[] = []) {
	return mappedPositions.some((mappedPosition) => mappedPosition?.payrollPosition?.includes(""));
}

/**
 * Position Mapping Layer - This component is responsible for rendering the Position Mapping Layer (Main Component).
 */
export default function PositionMappingLayer() {
	// Hooks
	const queryClient = useQueryClient();
	const { addSubscription, getSubscription } = useSubscriptions();
	const { useAuth } = useAuthorization();
	const hasEditPermission: boolean = useAuth(actions.EDIT_POSITION_MAPPINGS);
	const { enqueueSnackbar } = useSnackbar();

	const renderSnackbarsActions = (key: SnackbarKey) => {
		return <SnackbarsActions key={key.toString()} index={key} />;
	};

	const showSnackBar = (type: "error" | "success", message: string, critical: boolean | undefined) => {
		enqueueSnackbar(<SnackbarsContent message={message} critical={critical} />, {
			variant: type,
			preventDuplicate: true,
			action: renderSnackbarsActions,
		});
	};

	// Global State (Redux Store)
	const locationId = useLocationId();

	// States
	const [allPositionsMapping, setAllPositionsMapping] = useState<IPositionsMapping>({
		managedPositions: [],
		requiredPositions: [],
		accountingPositions: [],
		availablePositions: [],
	});
	const [disabledOtherClients, setDisabledOtherClients] = useState<boolean>(false);

	/********************************************* QUERIES ********************************************/
	const { data, isLoading } = useQuery({
		queryKey: ["positionsMapping", locationId],
		queryFn: () => getPositionsMapping(locationId),
		enabled: Boolean(locationId),
	});

	const { managedPositions, requiredPositions, accountingPositions, availablePositions } = allPositionsMapping;

	const selectablePositions = getAvailablePositions(allPositionsMapping);

	/******************************************** MUTATIONS ********************************************/
	const { mutate: applyMapping, isPending: isApplyingMapping } = useMutation({
		mutationKey: ["updatePositionsMapping", locationId],
		mutationFn: async (input: IMappedPositionInput) => {
			await updateSystemSettingsMappedPositions(locationId, input);
			await applyMappedPositions(locationId);
		},
		onSuccess: () => {
			showSnackBar("success", "The mapping was successfully applied.", false);
		},
		onError: () => {
			showSnackBar("error", "An error occurred trying to apply the mapping.", false);
		},
	});

	/********************************************* SUBSCRIPTIONS ********************************************/
	const positionMappingPageSubscription = getSubscription(["positionsMapping", locationId]);
	positionMappingPageSubscription?.subscriptions?.onUpdateSystemSettingsPositions?.setCallback((response: TSubscriptionResponse) => {
		const positionsMappingResponse = response.data.onUpdateSystemSettingsPositions;
		if (!isApplyingMapping) {
			disableButtonToOtherClients(positionsMappingResponse.locationId, true);
		}

		const simplifiedMappedPositions = managedPositions.map((mappedPosition) => mappedPosition.systemPosition);

		const managedPositionsFiltered = positionsMappingResponse.mappedPositions.filter((mappedPosition) =>
			simplifiedMappedPositions.includes(mappedPosition.systemPosition)
		);

		const simplifiedRequiredPositions = requiredPositions.map((mappedPosition) => mappedPosition.systemPosition);
		const requiredPositionsFiltered = positionsMappingResponse.requiredPositions.filter((mappedPosition) =>
			simplifiedRequiredPositions.includes(mappedPosition.systemPosition)
		);

		const accountingPositionsResponse = positionsMappingResponse.accountingPositions;
		const accountingPositionMapped: IBasicMappedPosition[] = [
			{
				_id: "accounting",
				systemPosition: "Accounting Positions",
				payrollPosition: accountingPositionsResponse,
				isDepartmental: false,
			},
		];

		const theAvailablePositions = getAvailablePositions({
			managedPositions: managedPositionsFiltered,
			requiredPositions: requiredPositionsFiltered,
			accountingPositions: accountingPositionMapped,
			availablePositions,
		});

		queryClient.setQueryData(
			["positionsMapping", locationId],
			(
				cachedData: IPositionsMapping = {
					managedPositions: [],
					requiredPositions: [],
					accountingPositions: [],
					availablePositions: [],
				}
			) => {
				return {
					...cachedData,
					managedPositions: managedPositionsFiltered,
					requiredPositions: requiredPositionsFiltered,
					accountingPositions: accountingPositionMapped,
					availablePositions: theAvailablePositions,
				};
			}
		);
	});

	const applyMappedPositionsSubscription = getSubscription(["applyMappedPositions", locationId]);

	applyMappedPositionsSubscription?.subscriptions?.onCreateUsers?.setCallback((response: TOtherClientSubscriptionResponse) => {
		const applyMappedPositionsResponse = response.data.onCreateUsers;
		disableButtonToOtherClients(applyMappedPositionsResponse.locationId, false);
	});

	type THandleSetAllPositionsMapping = {
		sectionId: keyof IPositionsMapping;
		updatedMappedPositions: IBasicMappedPosition[];
		updatedAvailablePositions?: LabelObject[];
	};

	/************************************************ HANDLERS **********************************************/
	const handleSetAllPositionsMapping = ({
		sectionId,
		updatedMappedPositions,
		updatedAvailablePositions,
	}: THandleSetAllPositionsMapping): void => {
		setAllPositionsMapping((prev: IPositionsMapping | null) => {
			const currentPositionsMapping = prev ?? {
				managedPositions: [],
				requiredPositions: [],
				accountingPositions: [],
				availablePositions: [],
			};

			return {
				...currentPositionsMapping,
				[sectionId]: updatedMappedPositions,
				availablePositions: updatedAvailablePositions || currentPositionsMapping.availablePositions,
			};
		});
	};

	const handleApplyMapping = () => {
		const accountingPositionsFlattened = accountingPositions.map((item) => item.payrollPosition).flat();

		const input: IMappedPositionInput = {
			requiredPositions,
			mappedPositions: managedPositions,
			accountingPositions: accountingPositionsFlattened,
		};

		applyMapping(input);
	};

	const handleRestoreConfiguration = () => {
		const previousPositionMapping = queryClient.getQueryData<IPositionsMapping>(["positionsMapping", locationId]) as IPositionsMapping;
		setAllPositionsMapping(previousPositionMapping);
	};

	const positionSections = [
		{
			id: "managedPositions" as keyof IPositionsMapping,
			title: "Managed Positions",
			isDropdownRequired: true,
			mappedPositions: managedPositions,
			selectablePositions,
		},
		{
			id: "requiredPositions" as keyof IPositionsMapping,
			title: "Required Positions",
			mappedPositions: requiredPositions,
			isDropdownRequired: false,
			disableClearable: true,
			selectablePositions,
		},
		{
			id: "accountingPositions" as keyof IPositionsMapping,
			title: "Accounting Positions",
			mappedPositions: accountingPositions,
			isDropdownRequired: false,
			selectablePositions,
		},
	];

	function disableButtonToOtherClients(locationIdParam: string, value: boolean) {
		if (locationId === locationIdParam) {
			setDisabledOtherClients(value);
		}
	}

	useEffect(() => {
		if (!locationId) {
			return;
		}

		addSubscription(
			["positionsMapping", locationId],
			[
				{
					context: "onUpdateSystemSettingsPositions",
					query: onUpdateSystemSettingsPositionMapping,
					variables: {
						locationId,
					},
				},
			]
		);

		addSubscription(
			["applyMappedPositions", locationId],
			[
				{
					context: "onCreateUsers",
					query: onCreateUsers,
					variables: {
						locationId,
					},
				},
			]
		);
	}, [locationId]);

	useEffect(() => {
		if (data) {
			setAllPositionsMapping((prev) => ({
				...prev,
				managedPositions: data.managedPositions,
				requiredPositions: data.requiredPositions,
				accountingPositions: data.accountingPositions,
				availablePositions: getAvailablePositions(data),
			}));
		}

		return () => {
			setAllPositionsMapping({
				managedPositions: [],
				requiredPositions: [],
				accountingPositions: [],
				availablePositions: [],
			});
		};
	}, [data]);

	const hasErrors = positionSections.some((section) => arePositionsUnmapped(section.mappedPositions));

	const stateButtonApplyMapping = !hasEditPermission || isApplyingMapping || hasErrors;
	const isPageLoading = isLoading || isApplyingMapping || !locationId;

	return (
		<BodyLayer>
			<RoundedBox sx={{ minHeight: "630px" }}>
				<Stack>
					<Box
						component={"form"}
						sx={{
							position: "absolute",
							right: "50px",
						}}
					>
						{isLoading ? (
							<Skeleton variant="rectangular" width={115} height={30} sx={{ borderRadius: "4px" }} />
						) : (
							<Stack direction={"row"} spacing={4}>
								<Button
									variant="contained"
									className={clsx({
										"h-[30px] !rounded-md !shadow-none !bg-[#585B6D]": true,
										"hover:!shadow-[0px_0px_6px_2px_rgba(88,91,109,0.5)]": hasEditPermission,
									})}
									onClick={handleRestoreConfiguration}
								>
									<Typography className="select-plan-dialog-assign-btn-text">Reload Configuration</Typography>
								</Button>
								<Button
									variant="contained"
									className={clsx({
										"h-[30px] !rounded-md !shadow-none": true,
										"hover:!shadow-primary-shadow-50": hasEditPermission,
										"!bg-primary": !stateButtonApplyMapping && !disabledOtherClients,
										"!bg-disabled": stateButtonApplyMapping || disabledOtherClients,
									})}
									onClick={() => handleApplyMapping()}
									disabled={stateButtonApplyMapping || disabledOtherClients}
								>
									{isApplyingMapping ? (
										<CircularProgress sx={{ color: "white", marginX: "28px" }} size={20} />
									) : (
										<Typography className="select-plan-dialog-assign-btn-text">Apply Mapping</Typography>
									)}
								</Button>
							</Stack>
						)}
					</Box>
					{positionSections.map((section, index) => (
						<Fragment key={section.id}>
							<PositionMappingSection
								{...section}
								hasEditPermission={hasEditPermission}
								hasErrors={hasErrors}
								isLoading={isPageLoading}
								isMutating={isApplyingMapping}
								// Callbacks
								onSetAllPositionsMapping={handleSetAllPositionsMapping}
							/>
							{index < positionSections.length - 1 && (
								<Divider className="!border-disabled" key={section.id} sx={{ mt: 5, mb: 3 }} />
							)}
						</Fragment>
					))}
				</Stack>
			</RoundedBox>
		</BodyLayer>
	);
}
